import { AppImageDimension, AppImages } from "app/appImages";
import { FeedAttachDto, FeedItemDto, PublicationAttachType, PublicationClassificationType } from "generated/CoreApi_v1";
import MediaUtils from "lib/MediaUtils";
import { SessionService } from "services/sessionService";

export type FeedItemAttachInfo = {
    attachId: string;
    image: ImageProps;
    thumbnailUrl?: string;
    presentationIndex?: number;
    showPlayIcon?: boolean;
};

export class FeedAttachHelper {
    private at: FeedAttachDto;

    constructor(at: FeedAttachDto) {
        this.at = at;
    }

    getAsset() {
        return {
            url: this.at.attachType == PublicationAttachType.YouTube
                ? MediaUtils.buildYoutubeEmbedUrl(this.at.linkExternalId!)
                : this.at.attachType == PublicationAttachType.Vimeo
                    ? this.at.linkSourceUrl!
                    : this.at.attachType == PublicationAttachType.Video && this.at.fileEncodedUrls?.hlsUri != null
                        ? this.at.fileEncodedUrls.hlsUri
                        : this.at.attachType == PublicationAttachType.Video && this.at.fileEncodedUrls?.progressiveDownloadUris != null && this.at.fileEncodedUrls?.progressiveDownloadUris.length > 0
                            ? SessionService.current.getApiFullUrl(this.at.fileEncodedUrls.progressiveDownloadUris[0]) as string
                            : SessionService.current.getAssetUrl(this.at.fileId)!,
            width: this.at.fileWidth ?? this.at.linkWidth ?? 0,
            height: this.at.fileHeight ?? this.at.linkHeight ?? 0,
            duration: this.at.fileDuration ?? this.at.linkDuration ?? 0,
            mediaTracks: this.at.fileMediaTracks
        };
    }

    getPosterUrl() {
        return this.at.attachType == PublicationAttachType.Video || this.at.attachType == PublicationAttachType.YouTube || this.at.attachType == PublicationAttachType.Vimeo
            ? this.at.filePosterUrl ?? this.at.linkThumbnailUrl ?? (this.at.playable ? AppImages.VideoMp4Poster : AppImages.VideoProcessingPoster)
            : this.at.attachType == PublicationAttachType.Audio
                ? SessionService.current.getAssetUrl(this.at.cardImageId) ?? SessionService.current.getAssetUrl(this.at.thumbnailId) ?? AppImages.AudioPoster
                : undefined;
    }

    getThumbnail() {
        return {
            url: SessionService.current.getAssetUrl(this.at.thumbnailId)
                ?? SessionService.current.getAssetUrl(this.at.cardImageId)
                ?? this.at.filePosterUrl
                ?? this.at.linkThumbnailUrl
                ?? (this.at.attachType == PublicationAttachType.Image
                    ? AppImages.EmptyImageThumbnail
                    : this.at.attachType == PublicationAttachType.Audio
                        ? AppImages.AudioThumbnail
                        : this.at.playable
                            ? AppImages.VideoMp4Thumbnail
                            : AppImages.VideoProcessingThumbnail
                ),
            width: this.at.thumbnailWidth ?? AppImageDimension.ThumbnailLg.width,
            height: this.at.thumbnailHeight ?? AppImageDimension.ThumbnailLg.height
        };
    }

    getCardImage() {
        var cardImage = SessionService.current.getAssetUrl(this.at.cardImageId);
        return {
            url: cardImage ?? this.getPosterUrl() ?? AppImages.UnknownImageThumbnail,
            width: AppImageDimension.CardPicture.width,
            height: AppImageDimension.CardPicture.height
        };
    }

    getTitle() {
        return this.at.fileTitle ?? this.at.linkTitle;
    }

    getDescription() {
        return this.at.caption ?? this.at.fileDescription ?? this.at.linkDescription;
    }

    isAudio() {
        return this.at.attachType == PublicationAttachType.Audio;
    }

    isVideo() {
        return [PublicationAttachType.Video, PublicationAttachType.Vimeo, PublicationAttachType.YouTube].includes(this.at.attachType);
    }

    isHls() {
        return this.at.fileEncodedUrls?.hlsUri != null;
    }

    useMediaPlayer() {
        return this.at.playable ?? false;
    }
}

export default class FeedItemHelper {
    private item: FeedItemDto;

    constructor(item: FeedItemDto) {
        this.item = item;
    }

    get hasPresentation() {
        return this.item.classificationType == PublicationClassificationType.Presentation && this.item.presentation && this.item.presentation.orderedItems.length > 1;
    }

    get hasAutoPlayPresentation() {
        return this.item.classificationType == PublicationClassificationType.Presentation && this.item.presentation && this.item.presentation.orderedItems.length > 1 && this.item.presentation.rotationInterval > 0;
    }

    get hasMedia() {
        return this.item.attachs && this.item.attachs.filter(p => this.isPresentationAttach(p.attachType) && !p.isEmbed).length > 0;
    }

    get hasOneImageOnly() {
        return this.item.attachs && this.item.attachs.filter(p => p.attachType == PublicationAttachType.Image && !p.isEmbed).length == 1;
    }

    get hasOneVideoOnly() {
        return this.item.attachs && this.item.attachs.filter(p => [PublicationAttachType.Video, PublicationAttachType.YouTube, PublicationAttachType.Vimeo].includes(p.attachType)).length == 1;
    }

    get hasOneAudioOnly() {
        return this.item.attachs && this.item.attachs.filter(p => p.attachType == PublicationAttachType.Audio).length == 1;
    }

    nonPresentationAttachs() {
        return this.item.attachs?.filter(p => !this.isPresentationAttach(p.attachType)) ?? [];
    }

    isPresentationAttach(at: PublicationAttachType) {
        return (at == PublicationAttachType.Image
            || at == PublicationAttachType.Audio
            || at == PublicationAttachType.Video
            || at == PublicationAttachType.YouTube
            || at == PublicationAttachType.Vimeo);
    }

    cardImage(): FeedItemAttachInfo | undefined {
        var attach = this.item.attachs?.find(p => [PublicationAttachType.Video, PublicationAttachType.YouTube, PublicationAttachType.Vimeo].includes(p.attachType))
            ?? this.item.attachs?.find(p => p.attachType == PublicationAttachType.Audio)
            ?? this.item.attachs?.find(p => p.attachId == this.item.firstImageAttachId);

        if (attach != null) {
            const at = this.getAttach(attach);
            const img = at.getCardImage();
            return {
                attachId: attach.attachId,
                image: {
                    url: img.url,
                    width: img.width,
                    height: img.height
                },
                thumbnailUrl: at.getThumbnail().url,
                presentationIndex: this.item.presentation?.orderedItems.findIndex(p => p == attach?.attachId),
                showPlayIcon: attach.playable ?? false
            };
        }

        return undefined;
    }

    getAttach(at: FeedAttachDto) {
        return new FeedAttachHelper(at);
    }
}
